import { FC, KeyboardEvent, MouseEvent, SyntheticEvent, useCallback, useEffect, useRef } from "react";
import { OptionProps } from "react-select";

import { Checkbox, CheckboxValue } from "capitalrx-components-2.0/lib/forms/Checkbox";
import { CustomReactSelectProps } from "capitalrx-components-2.0/lib/forms/Dropdown/CustomReactSelectProps";
import { OptionItem } from "capitalrx-components-2.0/lib/forms/Dropdown/OptionItem";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import classNames from "classnames";

import { Icon } from "../../../../general";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedDropdownOptionStyles = createComponentStyleHook<OptionProps<OptionItem | null>>({
  legacyStyles,
  tokenStyles,
  componentTokens: "dropdown_option",
});

/**
 * A single option within the select.
 */
export const Option: FC<OptionProps<OptionItem | null>> = (props) => {
  const { children, innerProps, innerRef, isSelected, selectProps, data } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedDropdownOptionStyles(props);
  const { enableCheckboxOptions, isItemSelected, onRemove } = selectProps as CustomReactSelectProps;

  const ref = useRef<HTMLDivElement | null>(null);
  const optionValue = data?.value;

  const optionClassName = classNames(styles.Option, "option");

  const handleRef = useCallback((value: HTMLDivElement | null) => {
    ref.current = value;
    innerRef?.(value);
  }, []);

  const handleRemove = useCallback(() => {
    onRemove?.(optionValue);
  }, [optionValue, onRemove]);

  const handleToggle = useCallback(
    (event?: SyntheticEvent) => {
      if (!enableCheckboxOptions) return;

      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const isChecked = isItemSelected?.(optionValue) ?? false;
      if (isChecked) {
        handleRemove();
        return;
      }

      innerProps?.onClick?.(event as MouseEvent<HTMLDivElement>);
    },
    [enableCheckboxOptions, handleRemove, innerProps, isItemSelected, optionValue],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!enableCheckboxOptions) return;
      if (event.key === "Enter") {
        handleToggle(event);
      }
    },
    [enableCheckboxOptions, handleToggle],
  );

  const handleCheckboxChange = useCallback(
    (_value: CheckboxValue) => {
      handleToggle();
    },
    [handleToggle],
  );

  useEffect(() => {
    if (!ref.current) return;

    let current: HTMLElement | null = ref.current;
    while (current) {
      if (current.classList.contains("menu")) break;
      current = current.parentElement;
    }

    if (!current || current === ref.current) return;

    const menuBounds = current.getBoundingClientRect();
    const optionBounds = ref.current.getBoundingClientRect();

    if (optionBounds.top < menuBounds.top || optionBounds.bottom > menuBounds.bottom) {
      if (optionBounds.top < menuBounds.top) {
        current.scrollBy(0, optionBounds.top - menuBounds.top);
      } else if (optionBounds.bottom > menuBounds.bottom) {
        current.scrollBy(0, optionBounds.bottom - menuBounds.bottom);
      }
    }
  }, []);

  const isChecked = isItemSelected?.(optionValue) ?? false;

  return (
    <StyleModeBoundary mode={mode}>
      <div
        {...innerProps}
        ref={handleRef}
        className={optionClassName}
        role="option"
        aria-selected={enableCheckboxOptions ? isChecked : isSelected}
        onClick={enableCheckboxOptions ? handleToggle : innerProps?.onClick}
        onKeyDown={handleKeyDown}
      >
        <div className={classNames(styles.Content, "content")}>
          {enableCheckboxOptions ? (
            <Checkbox value={isChecked} onChange={handleCheckboxChange} hideLabel unpadded label={children} />
          ) : (
            <>
              {children}
              {isSelected && <Icon name="check-mark" />}
            </>
          )}
        </div>
      </div>
    </StyleModeBoundary>
  );
};
